import { createRouter, createWebHistory } from 'vue-router'
import { trackRouter } from 'vue-gtag-next'
import store from '@/store'
import { ENTER_SITE } from '@/store/action-types'
import { SET_USER_DATA } from '@/store/mutation-types'

const Dashboard = () => import('@/views/Dashboard/Dashboard.vue')
const Register = () => import('@/views/Register/Register.vue')
const ManageAccounts = () => import('@/views/AccountManagement/Dashboard.vue')
const ManageAccounts_AccountsDashboard = () =>
  import('@/views/AccountManagement/Accounts/Dashboard.vue')
const ManageAccounts_AccountsManual = () =>
  import('@/views/AccountManagement/Accounts/Manual.vue')
const ManageAccounts_AccountsBulk = () =>
  import('@/views/AccountManagement/Accounts/Bulk.vue')
const ManageAccounts_SignDocuments = () =>
  import('@/views/AccountManagement/Documents/Sign.vue')
const ManageAccounts_ManageDocuments = () =>
  import('@/views/AccountManagement/Documents/Manage.vue')

const ClaimAuthorization = () =>
  import('@/views/ClaimAuthorization/ClaimAuthorization.vue')

const ContactManagement = () =>
  import('@/views/ContactManagement/ContactManagement.vue')

const Reports = () => import('@/views/Reports/Reports.vue')
const ReclaimDashboard = () =>
  import('@/views/Reports/ReclaimDashboard/ReclaimDashboard.vue')
const ReclaimDashboard_SummaryComponent = () =>
  import('@/views/Reports/ReclaimDashboard/tabs/Summary.vue')
const ReclaimDashboard_ActiveComponent = () =>
  import('@/views/Reports/ReclaimDashboard/tabs/Active.vue')
const ReclaimDashboard_PaidComponent = () =>
  import('@/views/Reports/ReclaimDashboard/tabs/Paid.vue')
const ReclaimDashboard_ExpiringComponent = () =>
  import('@/views/Reports/ReclaimDashboard/tabs/Expiring.vue')

const QuarterlyStatements = () =>
  import('@/views/Reports/QuarterlyStatements/QuarterlyStatements.vue')
const PaymentStatements = () =>
  import('@/views/Reports/PaymentStatements/PaymentStatements.vue')

const ReliefAtSource = () =>
  import('@/views/Reports/ReliefAtSource/ReliefAtSource.vue')
const ReliefAtSource_BenefitSummaryComponent = () =>
  import('@/views/Reports/ReliefAtSource/tabs/BenefitSummary.vue')
const ReliefAtSource_MissedOpportunitiesComponent = () =>
  import('@/views/Reports/ReliefAtSource/tabs/MissedOpportunities.vue')
const ReliefAtSource_AccountsComponent = () =>
  import('@/views/Reports/ReliefAtSource/tabs/Accounts.vue')

const DocumentManager = () =>
  import('@/views/DocumentManager/DocumentManager.vue')

const ErrorPage = () => import('@/views/ErrorPage/ErrorPage.vue')

const SiteMaintenance = () =>
  import('@/views/SiteMaintenance/SiteMaintenance.vue')

const DEEPLINK_SESSION_STORAGE_KEY = `gts-mc-deeplink__${__ENV}_v${__APP_VERSION}`

/**
 * Checks whether or not the user can access a task
 * @param {any} to
 * @returns Object or null (falsy check)
 */
const hasRights = function (to) {
  const _tasks = store.state.user.current.tasks

  let _allTasks = []
  for (const set in _tasks) {
    if (Object.hasOwnProperty.call(_tasks, set)) {
      _allTasks = _allTasks.concat(_tasks[set])
    }
  }

  const hasRights = _allTasks.find((el) => to.path.startsWith(el.route))

  return hasRights
}

/**
 * Redirects user to Unauthorized page
 * @param {function} next
 */
const kickThemOut = function (next) {
  return next('/error/unauthorized')
}

/**
 * Prevents access to a route based on 'permissions' to tasks.
 * If 'to' route does not match the allowed set of tasks aka Features,
 * user will be redirected to 403 page, otherwise allowed to proceed
 * @param {any} to
 * @param {any} from
 * @param {any} next
 */
const checkPermissions = function (to, from, next) {
  if (hasRights(to)) {
    next()
  } else {
    kickThemOut(next)
  }
}

const SITE_MAINTENANCE_URL = 'site-maintenance'

const routes = [
  {
    path: '/',
    name: 'home',
    component: Dashboard,
    meta: {
      auth: true,
    },
    beforeEnter(to, from, next) {
      let _tasks = store.state.user.current.tasks

      if (store.state.user.isAuthenticated) {
        // Send the user to Onboarding flow automatically
        if (store.state.user.current.isOnboarding) {
          next(_tasks.Management.find((m) => m.name === 'myAccounts').route)
        } else {
          next(
            _tasks.Features ? _tasks.Features[0].route : '/error/unauthorized',
          )
        }
      } else next()
    },
  },
  {
    path: '/register',
    name: 'register',
    component: Register,
    meta: {
      auth: false,
    },
  },
  {
    path: '/accounts',
    name: 'account-mgmt',
    component: ManageAccounts,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
    children: [
      {
        path: '/accounts/add',
        name: 'account-mgmt--accounts',
        component: ManageAccounts_AccountsDashboard,
        children: [
          {
            path: '/accounts/add/manual/:id?',
            name: 'account-mgmt--accounts-manual',
            component: ManageAccounts_AccountsManual,
          },
          {
            path: '/accounts/add/bulk',
            name: 'account-mgmt--accounts-bulk',
            component: ManageAccounts_AccountsBulk,
          },
        ],
      },
      {
        path: '/accounts/documents/:id?',
        name: 'account-mgmt--sign-docs',
        component: ManageAccounts_SignDocuments,
      },
      {
        path: '/accounts/documents/manage',
        name: 'account-mgmt--manage-docs',
        component: ManageAccounts_ManageDocuments,
      },
    ],
  },
  {
    path: '/claim-authorization',
    component: ClaimAuthorization,
    meta: {
      auth: true,
    },
    // beforeEnter: checkPermissions,
  },
  {
    name: 'ContactManagement',
    path: '/contacts/:email?',
    component: ContactManagement,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/reports',
    component: Reports,
    meta: {
      auth: true,
    },
    children: [
      {
        path: '/reports/reclaim-dashboard',
        redirect: '/reports/reclaim-dashboard/summary',
        name: 'reclaimDashboard',
        component: ReclaimDashboard,
        meta: {
          auth: true,
        },
        children: [
          {
            path: 'summary',
            name: 'summaryTab',
            component: ReclaimDashboard_SummaryComponent,
            meta: {
              auth: true,
            },
          },
          {
            path: 'active',
            name: 'activeTab',
            component: ReclaimDashboard_ActiveComponent,
            meta: {
              auth: true,
            },
          },
          {
            path: 'paid',
            name: 'paidTab',
            component: ReclaimDashboard_PaidComponent,
            meta: {
              auth: true,
            },
          },
          {
            path: 'expiring',
            name: 'expiringTab',
            component: ReclaimDashboard_ExpiringComponent,
            meta: {
              auth: true,
            },
          },
        ],
      },
      {
        path: 'quarterly-statements',
        component: QuarterlyStatements,
        meta: {
          auth: true,
        },
      },
      {
        path: 'payment',
        redirect: { name: 'paymentStatements' },
      },
      {
        path: 'payment-statements',
        name: 'paymentStatements',
        component: PaymentStatements,
        meta: {
          auth: true,
        },
      },
      {
        path: '/reports/relief-at-source',
        redirect: '/reports/relief-at-source/benefit-summary',
        name: 'reliefAtSource',
        component: ReliefAtSource,
        meta: {
          auth: true,
        },
        children: [
          {
            path: 'benefit-summary',
            name: 'reliefAtSourceBenefitSummaryTab',
            component: ReliefAtSource_BenefitSummaryComponent,
            meta: {
              auth: true,
            },
          },
          {
            path: 'missed-opportunities',
            name: 'reliefAtSourceMissedOpportunitiesTab',
            component: ReliefAtSource_MissedOpportunitiesComponent,
            meta: {
              auth: true,
            },
          },
          {
            path: 'accounts',
            name: 'reliefAtSourceAccountsTab',
            component: ReliefAtSource_AccountsComponent,
            meta: {
              auth: true,
            },
          },
        ],
      },
    ],
    beforeEnter(to, from, next) {
      // If user is internal (globetax), check if they are "refreshing" a report or going to View Reports
      if (store.state.user.current.isUserInternal) {
        return next()
      } else {
        // If the external user is going to a report OR "refreshing" it, let them go
        if (to.path !== '/reports') return next()

        // If the external user is going the View Reports from a refresh or "first entrance", check
        // available reports and redirect to 1st one. else, redirect to 500 page
        $.ajax({
          url: `${MC_API_URL}/reports`,
          method: 'POST',
          contentType: 'application/json',
          headers: {
            'X-HTTP-Method-Override': 'GET',
          },
          data: null,
        })
          .done((data) => {
            // AutoRedirect to 1st available report for external users
            next(data[0].route)
          })
          .fail(() => {
            next('/error/internal-server-error')
          })
      }
    },
  },
  {
    path: '/documents',
    component: DocumentManager,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/error/:errorType',
    name: 'error-page',
    component: ErrorPage,
  },
  {
    path: `/${SITE_MAINTENANCE_URL}`,
    name: SITE_MAINTENANCE_URL,
    component: SiteMaintenance,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// VueGtag - Google Analytics - Automatic Router tracking
trackRouter(router, {
  template(to, from) {
    return {
      // page_title: to.fullPath + ' (Custom Title)',
      page_path: to.path,
    }
  },
})

router.beforeEach((to, from, next) => {
  // Global Maintenance Block (note the return statements) of the Application
  if (window._MAINTENANCE && window._MAINTENANCE === true) {
    if (to.name !== SITE_MAINTENANCE_URL)
      return next({ name: SITE_MAINTENANCE_URL })
    else return next() // allow to proceed to Site Maintenance
  } else if (to.name === SITE_MAINTENANCE_URL) return next('/')

  const authenticated = store.state.user.isAuthenticated

  // Route Not Found case - bad link or user tampering with routes
  if (to.matched.length === 0) {
    // LOG BAD URLs explicitly
    if (authenticated) {
      $.post(`${MC_API_URL}/ui/log`, {
        eventType: 'UIInfo',
        eventDescription: `Page Not Found -- User Navigation to "${to.fullPath}" from "${from.fullPath}"`,
        applicationRoute: to.fullPath,
        applicationVersion: __APP_VERSION,
      })
    }

    next('/error/page-not-found')
    return
  }

  const authRequired = to.matched.some((route) => route.meta.auth)

  // If Page requires authentication and not authenticated, call GET USER
  if (authRequired && !authenticated) {
    // store deeplink in sessionStorage
    if (to.path !== '/') {
      window.sessionStorage.setItem(DEEPLINK_SESSION_STORAGE_KEY, to.fullPath)
    }

    // Try to call into the MIDAS API and get user info thus checking
    // whether we are really authenticated i.e. token cookie exists
    $.ajax({
      url: `${MC_API_URL}/user`,
      method: 'GET',
      async: false,
    })
      .done(function (response) {
        // If the Response is 200 OK, we enter the site and store the user information (json)
        store.dispatch(`user/${ENTER_SITE}`, response)

        // ENABLE chat when logged in user
        // const jiraWidgetKey = 'c1641e53-2ac3-4ce8-bb64-7dd1152f67f2'

        // const style = document.createElement('style')
        // style.textContent = `.powered-link {display:none !important;}.powered-text {display:none !important;}.authentication-section-home-view {display:none !important;}`

        // const chatWidget = document.querySelector('chat-widget')
        // chatWidget.onload = function () {
        //   const isChatWidgetVisible = !!chatWidget.shadowRoot.querySelector(
        //     '.chat-widget-wrapper'
        //   )
        //   if (isChatWidgetVisible) {
        //     chatWidget.shadowRoot.appendChild(style)

        //     return
        //   }

        //   // If the Chat widget is not visible, show the Jira Service Desk widget instead
        //   const jiraWidget = document.createElement('script')
        //   jiraWidget.setAttribute('data-jsd-embedded', '')
        //   jiraWidget.setAttribute('data-key', jiraWidgetKey)
        //   jiraWidget.setAttribute(
        //     'data-base-url',
        //     'https://jsd-widget.atlassian.com'
        //   )
        //   jiraWidget.setAttribute(
        //     'src',
        //     'https://jsd-widget.atlassian.com/assets/embed.js'
        //   )
        //   jiraWidget.onload = function () {
        //     window.document.dispatchEvent(
        //       new Event('DOMContentLoaded', { bubbles: true, cancelable: true })
        //     )
        //   }
        //   document.body.appendChild(jiraWidget)
        // }

        // Force exit to Site Maintenance (solves HARD refresh F5)
        window._MAINTENANCE = response.maintenanceMode
        if (window._MAINTENANCE) {
          next({ name: SITE_MAINTENANCE_URL })
          return
        }

        // Proceed to either deeplink or 'to' destination
        let _to = window.sessionStorage.getItem(DEEPLINK_SESSION_STORAGE_KEY)
        if (_to) {
          window.sessionStorage.removeItem(DEEPLINK_SESSION_STORAGE_KEY)
          if (_to !== to.fullPath) {
            router.push(_to)
            return
          } else {
            next()
          }
        } else {
          next()
        }
      })
      .fail(function (jqXHR) {
        if (
          jqXHR.status === 0 ||
          jqXHR.status.toString().search(/5[0-9][0-9]/) >= 0
        ) {
          next('/error/internal-server-error')
        }
      })
  } else if (authRequired && authenticated) {
    // console.log('beforeEach: ' + JSON.stringify(new Date()))
    // this will allow for time to read the cookie that was updated by token extension
    setTimeout(() => {
      // LOG UI NAVIGATION TODO
      $.post(`${MC_API_URL}/ui/log`, {
        eventType: 'UIInfo',
        eventDescription: `User Navigation from "${from.fullPath}"  to "${to.fullPath}"`,
        applicationRoute: to.fullPath,
        applicationVersion: __APP_VERSION,
      })

      // GET USER DATA ON EACH NAVIGATION
      $.get(`${MC_API_URL}/user`)
        .done(function (response) {
          // If the Response is 200 OK, we enter the site and store the user information (json)
          store.commit(`user/${SET_USER_DATA}`, response)

          // Force exit to Site Maintenance (solves SOFT navigation using SPA routing)
          window._MAINTENANCE = response.maintenanceMode
          if (window._MAINTENANCE) {
            return next({ name: SITE_MAINTENANCE_URL })
          } else {
            next()
          }
        })
        .fail(function (jqXHR) {
          if (
            jqXHR.status === 0 ||
            jqXHR.status.toString().search(/5[0-9][0-9]/) >= 0
          ) {
            next('/error/internal-server-error')
          }
        })
    }, 500)
  } else {
    // Authenticated or not Authentication needed - proceed to route
    next()
  }
})

export default router
